import React from "react";
import Select, {
	components,
	GroupBase,
	MenuListProps,
	OptionProps,
	ValueContainerProps,
	StylesConfig,
} from "react-select";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { CompanyFilterMode } from "@src/components/Header/Header.constants";
import { filtersConfig } from "@src/utils/reactSelectUtils";
import { SelectOption } from "./MultiSelectYup";

interface CompanyMultiSelectYupProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	options: Array<SelectOption<string>>;
	placeholder?: string;
	isLoading?: boolean;
	isDisabled?: boolean;
	maxSelected?: number;
	mode: CompanyFilterMode;
	onModeChange: (mode: CompanyFilterMode) => void;
}

interface CompanySelectExtraProps {
	companyMode: CompanyFilterMode;
	onToggleSelectAll: () => void;
	closedLabel: string;
	searchTerm: string;
	onSearchChange: (value: string) => void;
	onCloseMenu: () => void;
}

const CheckboxOption = (
	props: OptionProps<SelectOption<string>, true, GroupBase<SelectOption<string>>>,
) => {
	const { isSelected, data } = props;
	const inactive = data.isActive === false;

	return (
		<components.Option {...props}>
			<div style={{ display: "flex", alignItems: "center", gap: 8, opacity: inactive ? 0.55 : 1 }}>
				<input type="checkbox" checked={isSelected} readOnly />
				<span>{data.label}</span>
			</div>
		</components.Option>
	);
};

const HiddenMultiValue = () => null;

const CompanyMenuList = (
	props: MenuListProps<SelectOption<string>, true, GroupBase<SelectOption<string>>>,
) => {
	const { companyMode, onToggleSelectAll, searchTerm, onSearchChange, onCloseMenu } =
		props.selectProps as typeof props.selectProps & CompanySelectExtraProps;
	const checked = companyMode === CompanyFilterMode.ALL;

	return (
		<components.MenuList {...props}>
			<div
				style={{
					position: "sticky",
					top: 0,
					zIndex: 2,
					background: "#fff",
					padding: "8px 12px",
					borderBottom: "1px solid #E0E0E0",
					marginBottom: 4,
				}}
			>
				<input
					autoFocus
					type="text"
					value={searchTerm}
					placeholder="Search companies"
					onChange={(event) => onSearchChange(event.target.value)}
					onKeyDown={(event) => {
						event.stopPropagation();
						if (event.key === "Escape") {
							event.preventDefault();
							onCloseMenu();
						}
					}}
					style={{
						width: "100%",
						boxSizing: "border-box",
						padding: "4px 6px",
						marginBottom: 8,
						fontSize: 12,
						border: "1px solid #d8d5d5",
						borderRadius: 4,
						outline: "none",
					}}
				/>
				<label
					style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
					onMouseDown={(event) => event.preventDefault()}
				>
					<input
						ref={(node) => {
							if (node) {
								node.indeterminate = companyMode === CompanyFilterMode.EXCLUDE;
							}
						}}
						type="checkbox"
						checked={checked}
						onChange={onToggleSelectAll}
					/>
					<span style={{ fontSize: 11 }}>Select all</span>
				</label>
			</div>
			{props.children}
		</components.MenuList>
	);
};

const CompanyValueContainer = (
	props: ValueContainerProps<SelectOption<string>, true, GroupBase<SelectOption<string>>>,
) => {
	const { closedLabel } = props.selectProps as typeof props.selectProps & CompanySelectExtraProps;

	return (
		<components.ValueContainer {...props}>
			<div
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					maxWidth: "100%",
				}}
			>
				{closedLabel}
			</div>
		</components.ValueContainer>
	);
};

export const CompanyMultiSelectYup = <T extends FieldValues>({
	control,
	name,
	options,
	placeholder,
	isLoading,
	isDisabled,
	maxSelected = 20,
	mode,
	onModeChange,
}: CompanyMultiSelectYupProps<T>) => {
	const { field, fieldState } = useController({ name, control });
	const rootRef = React.useRef<HTMLDivElement | null>(null);
	const [menuIsOpen, setMenuIsOpen] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState("");

	const filteredOptions = React.useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return options;
		return options.filter((o) => o.label.toLowerCase().includes(term));
	}, [options, searchTerm]);

	const valueArray = React.useMemo(() => {
		return (Array.isArray(field.value) ? field.value : []) as Array<string>;
	}, [field.value]);

	const selectedOptions = React.useMemo(() => {
		if (!valueArray.length) return [];
		const set = new Set(valueArray);
		return options.filter((o) => set.has(o.value));
	}, [options, valueArray]);

	const closedLabel = React.useMemo(() => {
		const fallback = "All Companies";
		const includedCount = selectedOptions.length;
		const excludedCount = Math.max(options.length - includedCount, 0);

		if (mode === CompanyFilterMode.ALL) {
			return fallback;
		}

		if (mode === CompanyFilterMode.INCLUDE) {
			if (includedCount === 1) return selectedOptions[0].label || fallback;
			if (includedCount > 1) return `${includedCount} included`;
			return "Select companies";
		}

		if (excludedCount === 0) return fallback;
		if (includedCount === 1) return selectedOptions[0].label || fallback;
		return `${excludedCount} excluded`;
	}, [mode, options.length, selectedOptions]);

	React.useEffect(() => {
		if (!menuIsOpen) return;

		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			if (!(event.target instanceof Node)) return;
			if (!rootRef.current) return;
			if (!rootRef.current.contains(event.target)) {
				setMenuIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("touchstart", handlePointerDown);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("touchstart", handlePointerDown);
		};
	}, [menuIsOpen]);

	React.useEffect(() => {
		if (isDisabled && menuIsOpen) {
			setMenuIsOpen(false);
		}
	}, [isDisabled, menuIsOpen]);

	React.useEffect(() => {
		if (!menuIsOpen) {
			setSearchTerm("");
		}
	}, [menuIsOpen]);

	const { isDirty, invalid } = fieldState;
	const innerTernary = valueArray.length && isDirty ? "success" : "none";
	const inputStatus = invalid ? "error" : innerTernary;

	const customStyles = filtersConfig(inputStatus, "m") as StylesConfig<
		SelectOption<string>,
		true,
		GroupBase<SelectOption<string>>
	>;

	const menuStyles: StylesConfig<SelectOption<string>, true, GroupBase<SelectOption<string>>> = {
		...customStyles,
		menuList: (base, state) => ({
			...(customStyles.menuList ? customStyles.menuList(base, state) : base),
			paddingTop: 0,
		}),
	};

	const onToggleSelectAll = React.useCallback(() => {
		if (mode === CompanyFilterMode.INCLUDE) {
			onModeChange(CompanyFilterMode.ALL);
			field.onChange(options.map((x) => x.value));
			return;
		}

		onModeChange(CompanyFilterMode.INCLUDE);
		field.onChange([]);
	}, [field, mode, onModeChange, options]);

	return (
		<div
			ref={rootRef}
			onMouseDown={() => {
				if (!isDisabled && !menuIsOpen) {
					setMenuIsOpen(true);
				}
			}}
		>
			<Select<SelectOption<string>, true, GroupBase<SelectOption<string>>>
			isMulti
			isClearable={false}
			isDisabled={isDisabled ?? false}
			isSearchable={false}
			isLoading={isLoading ?? false}
			closeMenuOnSelect={false}
			menuIsOpen={menuIsOpen}
			onMenuOpen={() => setMenuIsOpen(true)}
			hideSelectedOptions={false}
			options={filteredOptions}
			placeholder={placeholder}
			styles={menuStyles}
			{
				...({
					companyMode: mode,
					onToggleSelectAll,
					closedLabel,
					searchTerm,
					onSearchChange: setSearchTerm,
					onCloseMenu: () => setMenuIsOpen(false),
				} as Record<string, unknown>)
			}
			components={{
				IndicatorSeparator: null,
				MenuList: CompanyMenuList,
				Option: CheckboxOption,
				ValueContainer: CompanyValueContainer,
				Placeholder: () => null,
				MultiValue: HiddenMultiValue,
				MultiValueRemove: HiddenMultiValue,
			}}
			onChange={(data) => {
				const next = (data ?? []).map((x) => x.value);
				const total = options.length;

				if (mode === CompanyFilterMode.INCLUDE) {
					if (next.length > maxSelected) {
						return;
					}
					field.onChange(next);
					onModeChange(CompanyFilterMode.INCLUDE);
					return;
				}

				const deselectedCount = total - next.length;
				if (deselectedCount > maxSelected) {
					return;
				}

				field.onChange(next);
				onModeChange(deselectedCount === 0 ? CompanyFilterMode.ALL : CompanyFilterMode.EXCLUDE);
			}}
			value={selectedOptions}
		/>
		</div>
	);
};
